import React, { useEffect, useState } from "react";
import { Button, DatePicker, Popconfirm, Select, Table, Tag, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { DeleteOutlined, EditOutlined, EyeFilled } from "@ant-design/icons";
import "../../css/common.css";
import { post } from "../../../services/services";
import { ApiURL, restHoursPdfUrl } from "../../../services/apiConstants";
import { ErrorToast, SuccessToast } from "../../../widgets/AppToast";
import VoyageDetails from "../../../components/VoyageDetails";
import { formatDate } from "../../../utils/dateFormatter";
import { DOWNLOADPDF } from "../../../utils/downloadPdf";
import dayjs from "dayjs";
import { dateRanges } from "../../../Data/data";
import useVoyageList from "../../../hooks/useVoyageList";

const { RangePicker } = DatePicker;

const ENTRY_TYPE_COLOR = {
  work: "blue",
  rest: "green",
  break: "orange",
  meal: "purple",
  overtime: "red",
};

const ListRestHours = () => {
  const navigate = useNavigate();
  const { voyageList } = useVoyageList();
  const [page_loader, set_page_loader] = useState(false);
  const [records, setRecords] = useState([]);
  const [staff_dashboard, set_staff_dashboard] = useState([]);
  const [voyage_ids, set_voyage_id] = useState("");
  const [voyage_status, set_voyage_status] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [dates, setDates] = useState([dayjs().subtract(7, "day"), dayjs()]);
  const [startDate, endDate] = dates?.map((d) => d.format("YYYY-MM-DD")) || [];

  useEffect(() => {
    const staffData = JSON.parse(localStorage.getItem("staff_dashboard") || "{}");
    set_staff_dashboard(staffData);
    LIST_REST_HOURS_API(1);
  }, []);

  useEffect(() => {
    if (voyageList?.length > 0) {
      const runningVoyage = voyageList.find((v) => v.voyage_status === true);
      set_voyage_status(runningVoyage?.voyage_status || false);
      if (runningVoyage) set_voyage_id(runningVoyage.id);
    }
  }, [voyageList]);

  const LIST_REST_HOURS_API = (page = 1) => {
    set_page_loader(true);
    const staff_Details = JSON.parse(localStorage.getItem("user_data"));
    const FORM_DATA = new FormData();
    FORM_DATA.append("ship_id", staff_Details?.data?.ship_details?.ship_id);
    FORM_DATA.append("page", page);
    FORM_DATA.append("start_date", startDate || "");
    FORM_DATA.append("end_date", endDate || "");
    try {
      post(ApiURL.list_rest_hours_st, FORM_DATA).then((res) => {
        if (res?.status === true) {
          setRecords(res.data || []);
          if (res.pagination) {
            setPagination({
              current: res.pagination.page,
              pageSize: res.pagination.page_size,
              total: res.pagination.total,
            });
          }
        }
        set_page_loader(false);
      });
    } catch (error) {
      console.error("Error fetching rest hours:", error);
      set_page_loader(false);
    }
  };

  const DELETE_REST_HOURS_API = (id) => {
    const FORM_DATA = new FormData();
    FORM_DATA.append("log_id", id);
    try {
      post(ApiURL.delete_rest_hours_st, FORM_DATA).then((res) => {
        if (res?.status === true) {
          SuccessToast("Entry deleted successfully");
          LIST_REST_HOURS_API(pagination.current);
        } else {
          ErrorToast("Failed to delete entry");
        }
      });
    } catch (error) {
      console.error("Error deleting entry:", error);
    }
  };

  const columns = [
    {
      title: "Id",
      dataIndex: "id",
      key: "id",
      align: "center",
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      align: "center",
      render: (text) => formatDate(text),
    },
    {
      title: "Start Time",
      dataIndex: "start_time",
      key: "start_time",
      align: "center",
    },
    {
      title: "End Time",
      dataIndex: "end_time",
      key: "end_time",
      align: "center",
    },
    {
      title: "Duration (hrs)",
      dataIndex: "duration",
      key: "duration",
      align: "center",
      render: (val) => (val !== undefined ? val.toFixed(1) : "—"),
    },
    {
      title: "Entry Type",
      dataIndex: "entry_type",
      key: "entry_type",
      align: "center",
      render: (type) => (
        <Tag color={ENTRY_TYPE_COLOR[type] || "default"} style={{ textTransform: "capitalize" }}>
          {type}
        </Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      align: "center",
      render: (text) => text || "—",
    },
    {
      title: "Action",
      align: "center",
      key: "Action",
      render: (_, record) => (
        <>
          <Tooltip title="View">
            <Button
              type="primary"
              size="small"
              onClick={() => navigate(`/view-rest-hours/${btoa(record?.id)}`)}
              style={{ marginRight: "5px" }}
              icon={<EyeFilled />}
            />
          </Tooltip>
          {voyage_status && (
            <>
              <Tooltip title="Edit">
                <Button
                  type="default"
                  size="small"
                  onClick={() => navigate(`/edit-rest-hours/${btoa(record?.id)}`)}
                  style={{ marginRight: "5px" }}
                  icon={<EditOutlined />}
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Popconfirm
                  title="Delete this entry?"
                  onConfirm={() => DELETE_REST_HOURS_API(record?.id)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="primary" danger size="small" icon={<DeleteOutlined />} />
                </Popconfirm>
              </Tooltip>
            </>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <VoyageDetails
        staff_dashboard={staff_dashboard?.voyage_details}
        buttonShow={true}
        disabled={!voyage_status}
        buttonProps={{
          text: "Add Entry",
          className: "save_btn",
          onClick: () => navigate("/add-rest-hours"),
          onPress: () => navigate("/audit-trail-rest-hours"),
        }}
      />

      <div className="row" style={{ marginBottom: "10px" }}>
        <div className="col-3" style={{ marginLeft: "15px" }}>
          <label className="block-label">Select Voyage</label>
          <Select
            placeholder="---- Select Voyage ----"
            style={{ width: "100%" }}
            value={voyage_ids}
            onChange={(value) => {
              const sel = voyageList.find((v) => v.id === value);
              if (sel) set_voyage_status(sel.voyage_status);
              set_voyage_id(value);
            }}
          >
            {voyageList?.map((voyage) => (
              <Select.Option key={voyage.id} value={voyage.id}>
                <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                  <span>{`Voyage Id: ${voyage.id}`}</span>
                  <span style={{ color: voyage.voyage_status ? "#FFA500" : "#008000", fontWeight: "550", marginRight: "15px" }}>
                    {voyage.voyage_status ? "Running" : "Complete"}
                  </span>
                </div>
              </Select.Option>
            ))}
          </Select>
        </div>

        <div className="col-3" />

        <div className="col-6" style={{ display: "flex", justifyContent: "flex-end" }}>
          <div style={{ marginRight: "15px" }}>
            <label className="block-label">Date Range</label>
            <RangePicker
              presets={dateRanges}
              value={dates}
              onChange={(val) => {
                setDates(val);
                setPagination((p) => ({ ...p, current: 1 }));
              }}
              format="YYYY-MM-DD"
              style={{ width: 330 }}
              disabledDate={(current) => current && current > dayjs().endOf("day")}
            />
          </div>
          <div style={{ alignSelf: "flex-end" }}>
            <Button
              type="primary"
              className="save_btn"
              style={{ color: "white", marginBottom: "13px", marginRight: "8px" }}
              onClick={() => LIST_REST_HOURS_API(1)}
            >
              Search
            </Button>
            <Button
              type="primary"
              className="save_btn"
              style={{ color: "white", marginBottom: "13px" }}
              onClick={() => DOWNLOADPDF(restHoursPdfUrl, voyage_ids, startDate, endDate)}
            >
              Download PDF
            </Button>
          </div>
        </div>
      </div>

      <Table
        bordered
        columns={columns}
        dataSource={records}
        rowKey="id"
        loading={page_loader}
        className="custom-table"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showTotal: (total) => `Total ${total} entries`,
          onChange: (page) => LIST_REST_HOURS_API(page),
        }}
      />
    </>
  );
};

export default ListRestHours;
